import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import  { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import { makeSeoData } from 'config/next-seo.config';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import React from 'react';

import SimpleCard from '../../components/cards/SimpleCard';
import HeroAlt from '../../components/hero-alt/HeroAlt';
import useStyles from './Brokerages.style';

const title = 'Brokerage Partnerships';
const description = 'We partner with top real estate brokerages to bring you the best possible service.';
const image = '/images/brokerages/hero.png';

function Brokerages() {
  const { theme, classes } = useStyles();
  const { md } = useBreakpoints();

  return (
    <>
      <AppBackgroundColor
        backgroundColor="#F7F7F7"
        borderColor="#F7F7F7"
        headerColor="#F7F7F7"
      />

      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <HeroAlt 
        title={
          <>
            Brokerage Partnerships 
            {' '}
            <br />
            {' '}
            with Guest House
          </>
          }
        subText={
          <>
            Elevate your listings. Retain top talent. Grow your business.
          </>
          }
        backgroundImage="/images/brokerages/hero.png"
        ctaButtons={[
          { 
            label: 'Inquire about a partnership',
            href: 'https://guesthouseshop-8563089.hs-sites-na2.com/book-now',
            variant: 'contained',
            color: 'secondary',
            target: '_blank',
          },
        ]}
      />

      <ContentBlock
        maxWidth={655}
        margin={md ? ['115px', 'auto'] : ['40px', 'auto']}
      >
        <Typography
          fontFamily={theme.gh_vars.circular}
          variant="body1"
          style={{ fontSize: md ? 24 : 18,textAlign: 'center' }}
        >
          Guest House is more than a staging company. We help brokerages move fast, build their brand and give agents an edge with premium services at exclusive prices.
        </Typography>
      </ContentBlock>
      
      <ContentBlock
        padding={md ? [15, 0, 15, 0] : [4, 0]}
        style={{ backgroundColor: '#E9E6E3' }}
        margin={[0]}
        maxWidth={'none'}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            display={md ? 'flex' : 'block'}
            alignItems="flex-start"
            justifyContent="center"
            gap={10}
            sx={{
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box
              flex={1}
              sx={{
                marginBottom: '32px',
                [theme.breakpoints.up('md')]: {
                  marginBottom: 0,
                },
              }}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: '24px' }}
              >
                Why partner with 
                {' '}
                <br />
                {' '}
                Guest House?
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                You have a vision for your brokerage. We&apos;re here to make it happen.
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                Guest House helps top brokerages and their agents earn more, sell faster and deliver exceptional service with every listing.
              </Typography>
              <a
                href="https://guesthouseshop-8563089.hs-sites-na2.com/book-now"
                style={{
                  display: 'inline-block',
                  fontWeight: 'bold',
                  backgroundColor: '#080808',
                  color: '#ffffff',
                  textDecoration: 'none !important',
                  borderRadius: '30px',
                  padding: md ? '16px 24px' : '12px 16px',
                  lineHeight: 1.4,
                  height: 'auto',
                  marginRight: '16px',
                  fontSize: md ? '20px' : '14px',
                  border: 0,
                }}
                target="_blank"
                rel="noreferrer"
              >
                Inquire about a partnership
              </a>
             
            </Box>
            <Box flex={1}>
              <SimpleCard
                title={
                  <b>
                    Level up every listing
                  </b>
                }
                description="Free your agents to focus on what they do best – selling – while we handle staging, photography, and prep with unmatched speed and consistency."
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Retention & recruiting"
              />
              <SimpleCard
                title={
                  <b>
                    Brand differentiation
                  </b>
                }
                description="Deliver a white-glove prep experience across your entire office, while maintaining brand standards and measurable performance data."
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Brand differentiation"
              />
              <SimpleCard
                title={
                  <b>
                    Faster sales in any market
                  </b>
                }
                description="Our staged homes consistently sell in under 30 days — often for up to 20% more –even when market conditions slow others down."
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Faster sales in tough markets"
              />
              <SimpleCard
                title={
                  <b>
                    Win more listings
                  </b>
                }
                description="Give your agents proof that sells: real results, beautiful case studies, and co-branded success stories that build confidence with every client."
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Win more listings"
              />
              
            </Box>
              
          </Box>
          
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={1400}
        margin={[0, 'auto']}
        padding={md ? [12, 0, 12, 0] : [4, 2]}
      >
        <Typography
          fontFamily={theme.gh_vars.circular}
          variant="h2"
          style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: md ? '24px' : '24px', textAlign: 'center' }}
        >
          Unlock exclusive partnership benefits
        </Typography>
        <Typography
          fontFamily={theme.gh_vars.circular}
          variant="h3"
          style={{ fontSize: md ? 24 : 18, marginBottom: md ? '64px' : '32px', textAlign: 'center' }}
        >
          As an official Guest House brokerage partner, you&apos;ll receive:
        </Typography>
        <Box className={classes.benefitsContainer}>
          <Box className={classes.benefitCard}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              component="h4"
            >
              1. Co-branded materials
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
            >
              Make it easy for your agents to access premium staging and listing prep, a perk that keeps talent loyal.
            </Typography>
          </Box>

          <Box className={classes.benefitCard}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              component="h4"
            >
              2. Marketing & sales enablement
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
            >
              Share data, case studies, and documents with sellers designed to win listings and drive investment into great home prep.
            </Typography>
          </Box>

          <Box className={classes.benefitCard}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              component="h4"
            >
              3. Priority scheduling
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
            >
              Ensure your agents get first access to staging—delivered in as little as 3 days.
            </Typography>
          </Box>

          <Box className={classes.benefitCard}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              component="h4"
            >
              4. Education and collaboration 
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
            >
              Leverage exclusive networking events, training sessions and collaborative campaigns.
            </Typography>
          </Box>

          <Box className={classes.benefitCard}>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
              component="h4"
            >
              5. Discounts and promotions
            </Typography>
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="body1"
            >
              Save money as you leverage Guest House for all of your listings
            </Typography>
          </Box>
        </Box>
        <Box sx={{ '& a': { textDecoration: 'none !important' }, textAlign: 'center', marginTop: md ? '64px' : '32px' }}>
          <a
            href="https://guesthouseshop-8563089.hs-sites-na2.com/book-now"
            style={{
              display: 'inline-block',
              fontWeight: 'bold',
              backgroundColor: '#080808',
              color: '#ffffff',
              textDecoration: 'none !important',
              borderRadius: '30px',
              padding: md ? '16px 24px' : '12px 16px',
              lineHeight: 1.4,
              height: 'auto',
              marginRight: '16px',
              fontSize: md ? '20px' : '14px',
              border: 0,
            }}
            target="_blank"
            rel="noreferrer"
          >
            Inquire about a partnership
          </a>
        </Box>
        
      </ContentBlock>

      <ContentBlock
        padding={md ? [15, 0, 15, 0] : [4, 0]}
        style={{ backgroundColor: '#F0F0F0' }}
        margin={[0]}
        maxWidth={'none'}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            display={md ? 'flex' : 'block'}
            alignItems="flex-start"
            justifyContent="center"
            gap={10}
            sx={{
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box
              flex={1}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: '24px' }}
              >
                Proven Results
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h3"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                Guest House has staged 2,000+ homes across Colorado, California, and Arizona—with results brokerages can rally around:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 3, marginBottom: '32px' }}>
                {/* <Image
                  src="/images/homepage/chart-circle-icon.png"
                  alt="Co-branded landing page"
                  width={md ? 24 : 18}
                  height={md ? 24 : 18}
                /> */}
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  sx={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                >
                  <span style={{ fontWeight: 'bold', backgroundColor: '#E0BFB0', padding: '2px 6px', borderRadius: '4px' }}>
                    3-Day Average Turnaround
                  </span>
                  <br />
                  from empty to market-ready.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 3, marginBottom: '32px' }}>
                {/* <Image
                  src="/images/homepage/chart-circle-icon.png"
                  alt="Co-branded landing page"
                  width={md ? 24 : 18}
                  height={md ? 24 : 18}
                /> */}
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  sx={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                >
                  <span style={{ fontWeight: 'bold', backgroundColor: '#E0BFB0', padding: '2px 6px', borderRadius: '4px' }}>
                    Less than 30 days on market
                  </span>
                  <br />
                  average time before receiving offers
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 3, marginBottom: '32px' }}>
                {/* <Image
                  src="/images/homepage/chart-circle-icon.png"
                  alt="Co-branded landing page"
                  width={md ? 24 : 18}
                  height={md ? 24 : 18}
                /> */}
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  sx={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                >
                  <span style={{ fontWeight: 'bold', backgroundColor: '#E0BFB0', padding: '2px 6px', borderRadius: '4px' }}>
                    Up to 20% Higher Sales Prices
                  </span>
                  <br />
                  when compared to unstaged homes.
                </Typography>
              </Box>
            </Box>
            <Box flex={1}>
              <Box sx={{ textAlign: 'center', backgroundColor: '#fff',padding: md ? '36px 64px' : '24px 40px', borderRadius: '20px' }}>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ fontSize: md ? 30 : 20, letterSpacing: '-1%', fontWeight: 450, marginBottom: '16px', lineHeight: 1.4 }}
                >
                  “Guest House&apos;s estimating system is extremely intuitive and quick to use – it&apos;s been great going into listing appointments knowing exactly how much staging will cost. In terms of service, their team has gone above and beyond to make sure the installation is as seamless as possible.”
                </Typography>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ fontSize: md ? 18 : 14,fontWeight: 450, marginBottom: md ? '64px' : '32px', lineHeight: 1.4 }}
                >
                  – Jared Blank, The Agency
                </Typography>
                <a
                  href="/home-staging/get-a-quote"
                  style={{
                    display: 'inline-block',
                    fontWeight: 'bold',
                    backgroundColor: '#080808',
                    color: '#ffffff',
                    textDecoration: 'none !important',
                    borderRadius: '30px',
                    padding: md ? '16px 24px' : '12px 16px',
                    lineHeight: 1.4,
                    height: 'auto',
                    marginRight: '16px',
                    fontSize: md ? '20px' : '14px',
                    border: 0,
                  }}
                >
                  Get an instant quote
                </a>
              </Box>              
            </Box>
              
          </Box>
          
        </ContentBlock>
      </ContentBlock>
      <ContentBlock
        padding={md ? [15, 0, 15, 0] : [4, 0]}
        style={{ backgroundColor: '#E9E6E3' }}
        margin={[0]}
        maxWidth={'none'}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            display={md ? 'flex' : 'block'}
            alignItems="flex-start"
            justifyContent="center"
            gap={10}
            sx={{
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box
              flex={1}
              sx={{
                marginBottom: md ? 0 : '32px',
              }}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: md ? '24px' : '16px' }}
              >
                Partner with
                <br />
                Guest House
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h3"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                Becoming an official Guest House 
                {' '}
                <br />
                {' '}
                partner is simple:
              </Typography>
              <a
                href="https://guesthouseshop-8563089.hs-sites-na2.com/book-now"
                style={{
                  display: 'inline-block',
                  fontWeight: 'bold',
                  backgroundColor: '#080808',
                  color: '#ffffff',
                  textDecoration: 'none !important',
                  borderRadius: '30px',
                  padding: md ? '16px 24px' : '12px 16px',
                  lineHeight: 1.4,
                  height: 'auto',
                  marginRight: '16px',
                  fontSize: md ? '20px' : '14px',
                  border: 0,
                }}
              >
                Inquire about a partnership
              </a>
            </Box>
            <Box flex={2}>
              <Box
                className={classes.partnerStep}
                sx={{ borderBottom: '1px solid #080808' }}
              >
                <Box >
                  <Typography
                    variant="h2"
                    component="p"
                    style={{ fontSize: md ? 60 : 24, letterSpacing: '-2%px', marginBottom: md ? '24px' : 0, fontWeight: 450 }}
                  >
                    1
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    component="p"
                    style={{ fontSize: md ? 24 : 18, marginBottom: '16px', marginTop: md ? '20px' : '10px' }}
                  >
                    Request a partnership
                  </Typography>
                  <Typography
                    variant="body1"
                    component="p"
                    style={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                  >
                    Start the conversation with our team.
                  </Typography>
                </Box>
              </Box>
              <Box
                className={classes.partnerStep}
                sx={{ borderBottom: '1px solid #080808' }}
              >
                <Box >
                  <Typography
                    variant="h2"
                    component="p"
                    style={{ fontSize: md ? 60 : 24, letterSpacing: '-2%px', marginBottom: md ? '24px' : 0, fontWeight: 450 }}
                  >
                    2
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    component="p"
                    style={{ fontSize: md ? 24 : 18, marginBottom: '16px', marginTop: md ? '20px' : '10px' }}
                  >
                    Get customized materials
                  </Typography>
                  <Typography
                    variant="body1"
                    component="p"
                    style={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                  >
                    Access a white-labeled hub and documents for your brokerage
                  </Typography>
                </Box>
              </Box>
              <Box
                className={classes.partnerStep}
                sx={{ borderBottom: '1px solid #080808' }}
              >
                <Box >
                  <Typography
                    variant="h2"
                    component="p"
                    style={{ fontSize: md ? 60 : 24, letterSpacing: '-2%px', marginBottom: md ? '24px' : 0, fontWeight: 450 }}
                  >
                    3
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    component="p"
                    style={{ fontSize: md ? 24 : 18, marginBottom: '16px', marginTop: md ? '20px' : '10px' }}
                  >
                    Empower your agents
                  </Typography>
                  <Typography
                    variant="body1"
                    component="p"
                    style={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                  >
                    Roll out discounts, materials, and trainings.
                  </Typography>
                </Box>
              </Box>
              <Box
                className={classes.partnerStep}
              >
                <Box >
                  <Typography
                    variant="h2"
                    component="p"
                    style={{ fontSize: md ? 60 : 24, letterSpacing: '-2%px', marginBottom: md ? '24px' : 0, fontWeight: 450 }}
                  >
                    4
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h3"
                    component="p"
                    style={{ fontSize: md ? 24 : 18, marginBottom: '16px', marginTop: '20px' }}
                  >
                    Grow your business
                  </Typography>
                  <Typography
                    variant="body1"
                    component="p"
                    style={{ fontSize: md ? 18 : 18, lineHeight: 1.4 }}
                  >
                    Track the impact on sales and team growth
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </ContentBlock>
      </ContentBlock>
    </>
  );
}

export default Brokerages;
